export const PRODUCT_UPDATES_API = '/api/admin/product-updates';

export type ProductUpdateStatus = 'draft' | 'approved' | 'published' | 'archived';

export const STATUSES: readonly ProductUpdateStatus[] = ['draft', 'approved', 'published', 'archived'];

export interface LinkedRoadmapItem {
  id: string;
  title: string;
  category: string;
  status: string;
  release_notes: string | null;
  shipped_at: string | null;
}

export interface ProductUpdate {
  id: string;
  title: string;
  summary: string;
  body_markdown: string;
  hero_image_url: string | null;
  push_title: string | null;
  push_body: string | null;
  status: ProductUpdateStatus;
  created_by: string | null;
  updated_by: string | null;
  approved_by: string | null;
  approved_at: string | null;
  published_by: string | null;
  published_at: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  post_to_blog: boolean;
  blog_post_id: string | null;
  roadmap_items: LinkedRoadmapItem[];
}

export interface ProductUpdateForm {
  title: string;
  summary: string;
  body_markdown: string;
  hero_image_url: string;
  push_title: string;
  push_body: string;
  post_to_blog: boolean;
  roadmap_item_ids: string[];
}

export const LIMITS = {
  title: 140,
  summary: 280,
  body_markdown: 20000,
  push_title: 80,
  push_body: 180,
} as const;

export const STATUS_LABEL: Record<ProductUpdateStatus, string> = {
  draft: 'Draft',
  approved: 'Approved',
  published: 'Published',
  archived: 'Archived',
};

export const STATUS_TONE: Record<ProductUpdateStatus, 'neutral' | 'info' | 'success' | 'warning'> = {
  draft: 'neutral',
  approved: 'info',
  published: 'success',
  archived: 'warning',
};

export interface Actions {
  canEdit: boolean;
  canApprove: boolean;
  canPublish: boolean;
  canArchive: boolean;
}

export function actionsFor(status: ProductUpdateStatus | null, writable: boolean): Actions {
  if (!writable) return { canEdit: false, canApprove: false, canPublish: false, canArchive: false };
  if (status === null) return { canEdit: true, canApprove: false, canPublish: false, canArchive: false };
  return {
    canEdit: status !== 'archived',
    canApprove: status === 'draft',
    canPublish: status === 'approved',
    canArchive: status !== 'archived',
  };
}

export function editNote(status: ProductUpdateStatus | null): string | null {
  if (status === 'approved') return 'Saving a change sends this update back to draft. Approve it again before publishing.';
  if (status === 'published') return 'This update is live. Saving changes what athletes read now; no new notification is sent.';
  if (status === 'archived') return 'Archived updates cannot be edited.';
  return null;
}

export function emptyForm(): ProductUpdateForm {
  return {
    title: '',
    summary: '',
    body_markdown: '',
    hero_image_url: '',
    push_title: '',
    push_body: '',
    post_to_blog: false,
    roadmap_item_ids: [],
  };
}

export function formFrom(update: ProductUpdate): ProductUpdateForm {
  return {
    title: update.title,
    summary: update.summary,
    body_markdown: update.body_markdown,
    hero_image_url: update.hero_image_url ?? '',
    push_title: update.push_title ?? '',
    push_body: update.push_body ?? '',
    post_to_blog: update.post_to_blog,
    roadmap_item_ids: update.roadmap_items.map((i) => i.id),
  };
}

export function validate(form: ProductUpdateForm): string[] {
  const problems: string[] = [];
  if (!form.title.trim()) problems.push('A title is required.');
  (Object.keys(LIMITS) as (keyof typeof LIMITS)[]).forEach((key) => {
    if (form[key].length > LIMITS[key]) problems.push(`${FIELD_LABEL[key]} is over ${LIMITS[key]} characters.`);
  });
  if (form.hero_image_url && !form.hero_image_url.startsWith('https://')) {
    problems.push('The hero image must be an https:// address.');
  }
  return problems;
}

const FIELD_LABEL: Record<keyof typeof LIMITS, string> = {
  title: 'The title',
  summary: 'The summary',
  body_markdown: 'The story',
  push_title: 'The notification title',
  push_body: 'The notification text',
};

type Payload = Partial<Record<keyof ProductUpdateForm, string | string[] | boolean | null>>;

function wire(form: ProductUpdateForm): Payload {
  return {
    title: form.title.trim(),
    summary: form.summary,
    body_markdown: form.body_markdown,
    hero_image_url: form.hero_image_url || null,
    push_title: form.push_title || null,
    push_body: form.push_body || null,
    post_to_blog: form.post_to_blog,
    roadmap_item_ids: form.roadmap_item_ids,
  };
}

export function createPayload(form: ProductUpdateForm): Payload {
  return wire(form);
}

export function changedPayload(form: ProductUpdateForm, original: ProductUpdate): Payload {
  const next = wire(form);
  const before = wire(formFrom(original));
  const out: Payload = {};
  (Object.keys(next) as (keyof Payload)[]).forEach((key) => {
    if (JSON.stringify(next[key]) !== JSON.stringify(before[key])) out[key] = next[key];
  });
  return out;
}

export function toggleItem(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}

export function moveItem(ids: string[], id: string, delta: -1 | 1): string[] {
  const at = ids.indexOf(id);
  const to = at + delta;
  if (at < 0 || to < 0 || to >= ids.length) return ids;
  const out = [...ids];
  [out[at], out[to]] = [out[to], out[at]];
  return out;
}

export function confirmPublishText(form: ProductUpdateForm): string {
  const blog = form.post_to_blog ? ' It will also be posted to the website blog.' : '';
  return `Publish "${form.title.trim()}"? Every athlete will see it in Feedback & Roadmap → Updates.${blog}`;
}

export function blogRetryNeeded(update: ProductUpdate | null, writable: boolean): boolean {
  return Boolean(writable && update && update.status === 'published' && update.post_to_blog && !update.blog_post_id);
}
